/* eslint-disable no-unused-vars, array-bracket-newline */
import { useCallback } from 'react';
import { ITicker, Network, enums } from '@kattana-io/common';
import Text from '../../../../atoms/Text';
import DesktopTable, {
  ITableSort,
} from '../../../../molecules/Table/DesktopTable';
import PairIcons from '../../../../molecules/PairIcons';
import WatchlistStar from '../WatchlistStar';
import ExchangeIcon from '../ChainExchangeSelector/ExchangeIcon';
import { useTableData } from './useTableData';
import useTicker from '../../../../hooks/useTicker';
import { formatBaseVolume, formatLiquidity, getPrice } from '../helpers';
import { formatAPY, formatAddress } from '../../../../helpers/formatters';
import { IRates } from '../../../../reducers/rates';
import NumberDisplay from '../../../../molecules/NumberDisplay';
import { getPriceChange } from '../../../../helpers/priceChange';

export const LS_TICKERS_SEARCH_KEY =
  'kattana-simple.tickers_search_modal_columns';

const sellClassName = 'row--Sell text--red-600';
const buyClassName = 'row--Buy text--green-600';

interface ITickersModalTableProps {
  currentId: number;
  tickers: ITicker[];
  loading: boolean;
  hiddenColumns: string[];
  isNextPageLoading: boolean;
  rates: IRates;
  width: number;
  tableSort: ITableSort;
  onTickerClick(ticker: ITicker): void;
  handleSort(id: string): void;
  onLoadNextPage(): void;
}

/**
 * Implements a table with trades
 * @param param0
 */
const TickersModalTable = ({
  tickers,
  currentId,
  loading,
  hiddenColumns,
  isNextPageLoading,
  rates,
  width,
  tableSort,
  onTickerClick,
  handleSort,
  onLoadNextPage,
}: ITickersModalTableProps) => {
  const { columns, data, tableColumnsWidth } = useTableData({
    tickers,
  });

  const renderCell = (cell: any) => {
    const rawTicker = cell.row.original;
    const item = useTicker(rawTicker);
    const price = getPrice(rawTicker, rates);
    const { priceChangePercent } = getPriceChange(price.rawPrice, rawTicker);

    switch (cell.column.id) {
    case 'pairs':
      return (
        <div className='tickers__panel__table__row__pair'>
          <PairIcons
            chain={item.chain}
            pair={item.name}
            base_address={item.base_address}
            quote_address={item.quote_address}
            base_slug={item.base_slug}
            quote_slug={item.quote_slug}
          />
          <Text size={14}>{item.name}</Text>
        </div>
      );
    case 'price_a_usd':
      return (
        <div className='table_price_change'>
          <NumberDisplay
            value={price.rawPrice}
            startSymbol={price.sign}
            textProps={{ size: 14 }}
          />
          {!!priceChangePercent && (
            <NumberDisplay
              value={priceChangePercent}
              round={2}
              startSymbol={priceChangePercent > 0 ? '+' : ''}
              endSymbol='%'
              textProps={{ size: 14 }}
            />
          )}
        </div>
      );
    case 'liquidity':
      return (
        <Text size={14}>
          {rawTicker.kind === enums.ETickerKind.DEX
            ? formatLiquidity(cell.value)
            : '-'}
        </Text>
      );
    case 'volume':
      return <Text size={14}>{formatBaseVolume(cell.value)}</Text>;
    case 'contract':
      return (
        <Text
          className={
            rawTicker.kind === enums.ETickerKind.DEX
              ? 'text--navy-600'
              : 'text--grey-400'
          }
          size={14}
        >
          {rawTicker.kind === enums.ETickerKind.DEX
            ? formatAddress(item.base_address)
            : 'CEX'}
        </Text>
      );
    case 'annual_percentage_yield':
      return <Text size={14}>{cell.value ? formatAPY(cell.value) : ''}</Text>;
    case 'kind':
      return (
        <>
          <div className='tickers__panel__table__row__exchange'>
            <ExchangeIcon
              exchange={cell.value}
              chain={item.chain}
              textProps={{ size: 14 }}
            />
          </div>
          <WatchlistStar ticker={rawTicker} />
        </>
      );
    case 'chain':
      return (
        <Text className='text--grey-400' size={14}>
          {Network.isValidChain(cell.value)
            ? Network.fromChain(cell.value).getTitle()
            : cell.value?.replace('_', ' ')}
        </Text>
      );
    default:
      return <Text size={14}>{cell.value}</Text>;
    }
  };

  /**
   * Highlight active ticker and change text color depending on price change
   */
  const getClassNameForRow = useCallback(
    (row: any) => {
      const classNames = ['tickers__panel__table__row'];
      const price = getPrice(row, rates);
      const { priceChangePercent } = getPriceChange(price.rawPrice, row);

      if (currentId == row.id) {
        classNames.push('tickers__panel__table__row--active');
      }
      if (priceChangePercent > 0) {
        classNames.push(buyClassName);
      }
      if (priceChangePercent < 0) {
        classNames.push(sellClassName);
      }
      if (priceChangePercent === 0) {
        classNames.push('text--grey-400');
      }

      return classNames.join(' ');
    },
    [currentId, rates]
  );

  /**
   * @param e - ListOnScrollProps
   * loads more rows if scrolled ${scrollPercentage} * 100% of content
   */
  const onScroll = useCallback(
    async (listHeight: number, scrollPos: number) => {
      const scrollPercentage = 0.9;

      if (scrollPos > listHeight * scrollPercentage) {
        if (tickers.length % 100 === 0 && !isNextPageLoading) {
          // if length % 100 !== 0 it means that the last loaded page had less than 100 props.items and it was the last generally
          onLoadNextPage();
        }
      }
    },
    [isNextPageLoading, tickers.length, onLoadNextPage]
  );

  return (
    <DesktopTable
      dataKey={LS_TICKERS_SEARCH_KEY}
      title='tickers'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      loading={loading}
      hiddenColumns={hiddenColumns}
      rememberColWidth={false}
      withTableControls={false}
      withBorder={false}
      sort={tableSort}
      rowHeight={36}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      onRowClick={onTickerClick}
      onScroll={onScroll}
      onSort={handleSort}
    />
  );
};

export default TickersModalTable;
